import { FC, useMemo } from 'react';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Box, Grid, Modal, Typography } from '@mui/material';
import { ModalHeader } from '~/components';
import { modalActionBlockStyles, modalContainerStyle, modalOverflowBoxStyles } from '~/components/styles';
import { useAppDispatch } from '~/redux';
import { closeModalById } from '~/redux/slices/modalSlice';

export interface FailedDetailsModalProps {
  payload: {
    id: string;
    details: string;
  };
}

const EXCEPTION_MARKER = 'Last few exceptions:';
const MAX_EXCEPTION_LINES = 5;

const normalizeDetails = (value: string) => value.replaceAll('\\n', '\n').replaceAll('\\t', '\t');

const parseDetails = (value: string) => {
  const normalized = normalizeDetails(value);
  const exitCodeMatch = normalized.match(/ExitCode:\s*(\d+)/i);
  const exitCode = exitCodeMatch?.[1];
  const markerIndex = normalized.indexOf(EXCEPTION_MARKER);
  const exitCodeIndex = exitCodeMatch?.index ?? -1;
  const summaryEnd = [markerIndex, exitCodeIndex].filter((index) => index > -1).sort()[0];
  const summarySource = summaryEnd ? normalized.slice(0, summaryEnd) : normalized;
  const summaryLine = summarySource.split('\n')[0].trim();
  const summary = summaryLine || 'Job failed. Check logs for details.';
  const exceptionText = markerIndex > -1 ? normalized.slice(markerIndex + EXCEPTION_MARKER.length) : '';
  const exceptionLines = exceptionText
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return {
    summary,
    exitCode,
    exceptionLines,
  };
};

const FailedDetailsModal: FC<FailedDetailsModalProps> = ({ payload: { id, details } }) => {
  const dispatch = useAppDispatch();
  const parsed = useMemo(() => parseDetails(details), [details]);

  const handleClose = () => {
    dispatch(closeModalById(id));
  };

  return (
    <Modal open onClose={handleClose}>
      <Box tabIndex={-1} sx={modalContainerStyle}>
        <ModalHeader onClose={handleClose} title={['Failed Details']} />
        <Grid container spacing={2} px={3} sx={modalOverflowBoxStyles}>
          <Grid size={12}>
            <Typography variant="h6">Summary</Typography>
            <Typography variant="body1">{parsed.summary}</Typography>
          </Grid>
          {parsed.exitCode && (
            <Grid size={12}>
              <Typography variant="h6">Exit Code</Typography>
              <Typography variant="body1">{parsed.exitCode}</Typography>
            </Grid>
          )}
          {parsed.exceptionLines.length && (
            <Grid container size={12}>
              <Typography variant="h6">Last Exceptions</Typography>
              <Box component="ul" sx={{ pl: 3, my: 0 }}>
                {parsed.exceptionLines.slice(0, MAX_EXCEPTION_LINES).map((line) => (
                  <Typography key={line} component="li" variant="body1" sx={{ wordBreak: 'break-word' }}>
                    {line}
                  </Typography>
                ))}
              </Box>
              {parsed.exceptionLines.length > MAX_EXCEPTION_LINES && (
                <Typography variant="body2">
                  ...and {parsed.exceptionLines.length - MAX_EXCEPTION_LINES} more lines
                </Typography>
              )}
            </Grid>
          )}
        </Grid>
        <Box sx={modalActionBlockStyles}>
          <CustomButton buttonType="borderless" label="Cancel" onClick={handleClose} />
        </Box>
      </Box>
    </Modal>
  );
};

export default FailedDetailsModal;
